import React, { useState, useEffect } from 'react';
import './Photography.css';

interface Photo {
  id: string;
  src: string;
  title: string;
  description?: string;
  location?: string;
  date?: string;
  camera?: string;
  category: string;
}

const Photography: React.FC = () => {
  useEffect(() => {
    document.title = 'Photography | Sourov Roy';
  }, []);

  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [filter, setFilter] = useState<string>('all');

  const S3_BASE_URL = 'https://s3.ap-south-1.amazonaws.com/sourovroy.me-content/gallery/images';

  const photos: Photo[] = [
    {
      id: '12',
      src: `${S3_BASE_URL}/squirrel.09.11.25.v1.jpg`,
      title: 'Palm Squirrel',
      description: 'An Indian palm squirrel pausing mid-climb.',
      location: 'Golconda, Telangana',
      date: '2025-11-02',
      category: 'wildlife'
    },
    {
      id: '10',
      src: `${S3_BASE_URL}/bloodsucker.09.11.25.v1.jpg`,
      title: 'Bloodsucker Lizard',
      description: 'An oriental garden lizard basking in noon with striking ochre tones.',
      location: 'Forestrek Park, Telangana',
      date: '2025-11-10',
      category: 'wildlife'
    },
    {
      id: '1',
      src: `${S3_BASE_URL}/colony.jpg`,
      title: 'Ant Colony',
      description: 'Fascinating world of ants working together in perfect harmony',
      location: 'Shantiniketan, WB',
      date: '2024-10-20',
      category: 'wildlife'
    },
    {
      id: '2',
      src: `${S3_BASE_URL}/cow.jpg`,
      title: 'Happy Cow',
      description: 'A young cow basking in dappled light and shadow in a sal tree forest near Sonajhuri Hat',
      location: 'Shantiniketan, WB',
      date: '2024-10-22',
      category: 'wildlife'
    },
    {
      id: '3',
      src: `${S3_BASE_URL}/squrrel.jpg`,
      title: 'Happy Squirrel',
      description: 'A curious squirrel among the rocks, feeding on coconut in its natural habitat',
      location: 'Hampi, Karnataka',
      date: '2024-10-28',
      category: 'wildlife'
    },
    {
      id: '4',
      src: `${S3_BASE_URL}/agama.jpg`,
      title: 'Agama Portrait',
      description: 'A detailed close-up of an agama basking on ancient stone ruins',
      location: 'Hampi, Karnataka',
      date: '2025-01-01',
      category: 'wildlife'
    },
    {
      id: '5',
      src: `${S3_BASE_URL}/geese.jpg`,
      title: 'Geese Gathering',
      description: 'A flock of geese foraging near the Tungabhadra River at golden hour',
      location: 'Hampi, Karnataka',
      date: '2025-01-01',
      category: 'wildlife'
    },
    {
      id: '6',
      src: `${S3_BASE_URL}/crows.jpg`,
      title: 'Crows in Flight',
      description: 'A flock of crows perched atop a dead tree moments before takeoff',
      location: 'Shantiniketan, WB',
      date: '2025-09-07',
      category: 'wildlife'
    },
    {
      id: '7',
      src: `${S3_BASE_URL}/morning_glory.jpg`,
      title: 'Morning Glory Bloom',
      description: 'Morning glory vine opening to the first light on the outskirts of the Deccan trap',
      location: 'Hyderabad, Telangana',
      date: '2025-10-25',
      category: 'nature'
    },
    {
      id: '8',
      src: `${S3_BASE_URL}/tungabhadra.jpg`,
      title: 'Tungabhadra River',
      description: 'Monsoon clouds mirrored on the tranquil Tungabhadra River at dawn',
      location: 'Hampi, Karnataka',
      date: '2025-08-01',
      category: 'nature'
    },
    {
      id: '9',
      src: `${S3_BASE_URL}/kans.jpg`,
      title: 'Kans Grass Fields',
      description: 'Late-monsoon kans grass swaying under a dramatic September sky',
      location: 'Shantiniketan, WB',
      date: '2025-09-07',
      category: 'nature'
    },
    {
      id: '11',
      src: `${S3_BASE_URL}/leadwort.09.11.25.v1.jpg`,
      title: 'Leadwort Blossoms',
      description: 'A cluster of vibrant leadwort flowers glowing in soft morning light.',
      location: 'Hyderabad, Telangana',
      date: '2025-11-10',
      category: 'nature'
    }
  ];

  const categories = ['all', 'nature', 'wildlife', 'architecture'];

  const filteredPhotos = filter === 'all' 
    ? photos 
    : photos.filter(photo => photo.category === filter);

  const openModal = (photo: Photo) => {
    setSelectedPhoto(photo);
  };

  const closeModal = () => {
    setSelectedPhoto(null);
  };

  const navigatePhoto = (direction: 'prev' | 'next') => {
    if (!selectedPhoto) return;
    
    const currentIndex = photos.findIndex(p => p.id === selectedPhoto.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % photos.length;
    } else {
      newIndex = (currentIndex - 1 + photos.length) % photos.length;
    }
    
    setSelectedPhoto(photos[newIndex]);
  };

  return (
    <div className="photography">
      <div className="photography-container">
        <div className="photography-header">
          <h1 className="photography-title">Photography</h1>
          <p className="photography-subtitle">
            Capturing moments and stories through the lens. Here's a collection of my favorite photographs.
          </p>
        </div>

        <div className="photography-filters">
          {categories.map(category => (
            <button
              key={category}
              className={`photo-filter-btn ${filter === category ? 'active' : ''}`}
              onClick={() => setFilter(category)}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        <div className="photography-grid">
          {filteredPhotos.map(photo => (
            <div 
              key={photo.id} 
              className="photo-item"
              onClick={() => openModal(photo)}
            >
              <div className="photo-wrapper">
                <img 
                  src={photo.src} 
                  alt={photo.title}
                  className="photo-image"
                  onError={(e) => {
                    // Fallback to a placeholder if image doesn't exist
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIENvbWluZyBTb29uPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
                <div className="photo-overlay">
                  <h3 className="photo-title">{photo.title}</h3>
                  <div className="photo-overlay-bottom">
                    <p className="photo-location">{photo.location}</p>
                    <p className="photo-date">
                      {photo.date ? new Date(photo.date).toLocaleDateString() : new Date('2025-01-01').toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {selectedPhoto && (
          <div className="photo-modal" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeModal}>×</button>
              <button className="modal-nav modal-prev" onClick={() => navigatePhoto('prev')}>‹</button>
              <button className="modal-nav modal-next" onClick={() => navigatePhoto('next')}>›</button>
              
              <div className="modal-image-container">
                <img 
                  src={selectedPhoto.src} 
                  alt={selectedPhoto.title}
                  className="modal-image"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIENvbWluZyBTb29uPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
              </div>
              
              <div className="modal-info">
                <h2 className="modal-title">{selectedPhoto.title}</h2>
                {selectedPhoto.description && (
                  <p className="modal-description">{selectedPhoto.description}</p>
                )}
                <div className="modal-details">
                  {selectedPhoto.location && (
                    <div className="modal-detail">
                      <strong>Location:</strong> {selectedPhoto.location}
                    </div>
                  )}
                  {selectedPhoto.date && (
                    <div className="modal-detail modal-detail-date">
                      <strong>Date:</strong> {new Date(selectedPhoto.date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Photography;
